import { useEffect, useState } from "react";
import { Check, Loader2 } from "lucide-react";
import api from "../lib/api";

// Pipeline stages recorded by the backend, in the order they run. `optional` stages don't run on every duck
// (spec is skipped when the feature spec already exists; render is skipped when the approved front is reused) — those
// rows are hidden rather than left blank. The legacy pipeline never records spec/render, so they are hidden there too.
export const BUILD_STAGES = [
  { name: "spec", label: "Reading your duck's features", optional: true },
  { name: "render", label: "Drawing the front render", optional: true },
  { name: "views", label: "Drawing back & side views" },
  { name: "upload", label: "Uploading views to the sculptor" },
  { name: "sculpt", label: "Sculpting the 3D mesh" },
  { name: "trim", label: "Trimming the flat base" },
  { name: "paint", label: "Painting the filament colours" },
  { name: "package", label: "Packaging your printable files" },
];

const openStage = (creation) => (creation.build_stages || []).find((s) => !s.ended_at);

export const stageLabel = (creation) => {
  const open = openStage(creation);
  return BUILD_STAGES.find((m) => m.name === open?.name)?.label || "Getting your duck in the queue";
};

const fmtDur = (s) => (s >= 60 ? `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s` : `${s}s`);
const fmtRough = (s) => (s >= 90 ? `${Math.round(s / 60)} min` : `${Math.round(s / 15) * 15} sec`);

const pace = (secs, est) => {
  if (!est) return null;
  if (secs > est * 1.3) return { text: `${fmtDur(Math.round(secs - est))} over the usual`, cls: "text-amber-600 dark:text-amber-400" };
  if (secs < est * 0.7) return { text: "ahead of schedule", cls: "text-green-600 dark:text-green-500" };
  return { text: "on schedule", cls: "text-muted-foreground" };
};

const Row = ({ meta, stage, status, paused, now, est }) => {
  const done = status === "done";
  const active = status === "active";
  // Duration only when this stage was actually timed. A stage we infer as done (e.g. an older duck that never
  // recorded "upload") still gets its check, just no duration.
  const secs = stage ? Math.max(0, Math.round(((stage.ended_at ? new Date(stage.ended_at).getTime() : now) - new Date(stage.started_at).getTime()) / 1000)) : 0;
  const hint = active && !paused ? pace(secs, est) : null;
  return (
    <li
      data-testid={active ? "build-stage" : `build-stage-${meta.name}`}
      data-status={status}
      className={`flex items-center gap-3 rounded-xl px-3 py-2 transition-colors ${
        active ? "bg-primary/10 text-foreground" : done ? "text-muted-foreground" : "text-muted-foreground/50"
      }`}
    >
      <span
        className={`grid place-items-center h-5 w-5 shrink-0 rounded-full border-2 ${
          done ? "border-accent bg-accent text-accent-foreground" : active ? "border-primary text-primary" : "border-current/40"
        }`}
      >
        {done ? <Check className="h-3 w-3" strokeWidth={3} /> : active && !paused ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
      </span>
      <span className={`flex-1 min-w-0 truncate text-sm ${active ? "font-semibold" : ""}`}>{meta.label}</span>
      {hint && (
        <span data-testid="build-stage-pace" className={`hidden sm:inline text-xs font-mono2 shrink-0 ${hint.cls}`}>
          {hint.text}
        </span>
      )}
      <span className="font-mono2 text-xs tabular-nums shrink-0">{stage ? fmtDur(secs) : ""}</span>
    </li>
  );
};

// Real pipeline checklist. A stage is DONE once a LATER stage has started (or the build is ready) — even if it never
// recorded an ended_at — so finished and in-flight ducks never get stuck on a blank step. A stage that never ran
// (optional, no entry, already passed) is skipped, and nothing after the active stage is ever ticked.
export const BuildStageList = ({ creation }) => {
  const [now, setNow] = useState(Date.now());
  const [times, setTimes] = useState(null);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    api
      .get("/public/stage-times")
      .then((r) => setTimes(r.data))
      .catch(() => setTimes(null));
  }, []);

  const byName = Object.fromEntries((creation.build_stages || []).map((s) => [s.name, s]));
  const paused = creation.build_status === "waiting";
  const ready = creation.build_status === "ready";
  let maxStarted = -1; // furthest stage that has an entry = the one in progress while building
  BUILD_STAGES.forEach((m, i) => {
    if (byName[m.name]) maxStarted = i;
  });

  const statusFor = (i, meta) => {
    if (!ready && i === maxStarted) return "active";
    const past = ready || i < maxStarted; // this stage has already been passed
    if (past) return byName[meta.name] ? "done" : meta.optional ? "skip" : "done";
    return "pending"; // not reached yet
  };

  return (
    <div className="rounded-2xl border border-border/70 bg-card/60 p-2">
      {times?.total && (
        <p data-testid="build-eta" className="px-3 pt-1 pb-2 text-xs font-mono2 text-muted-foreground">
          Usually finishes in ~{fmtRough(times.total)}
          {times.samples > 1 && <span className="text-muted-foreground/70"> · from the last {times.samples} builds</span>}
        </p>
      )}
      <ol data-testid="build-stage-list" className="space-y-0.5">
        {BUILD_STAGES.map((meta, i) => {
          const status = statusFor(i, meta);
          if (status === "skip") return null;
          return <Row key={meta.name} meta={meta} stage={byName[meta.name]} status={status} paused={paused} now={now} est={times?.stages?.[meta.name]} />;
        })}
      </ol>
    </div>
  );
};
